import { useCallback, useRef, useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'

import type { AudioPlayerHandle } from '../../../components/AudioPlayer'
import type {
  VisualMediaPlayerDiagnostics,
  VisualMediaPlayerHandle,
} from './VisualMediaPlayer'
import { AudioPlayer } from '../../../components/AudioPlayer'
import { Spinner } from '../../../components/ui/Spinner'
import { MarkdownText } from './MarkdownText'
import { MediaImage } from './MediaImage'
import { SessionStepIndicator } from './SessionStepIndicator'
import { VisualMediaPlayer } from './VisualMediaPlayer'

export function SessionScreenContent({
  audioUrl,
  currentStepIndex,
  description,
  footerBottomPadding,
  initialVisualAspectRatio,
  onAudioLoadingChange,
  onNextPress,
  primaryLabel,
  scrollContentBottomPadding,
  status,
  stepKeys,
  stepTitle,
  visualContentType,
  visualDiagnostics,
  visualFallbackAspectRatio,
  visualUrl,
}: {
  audioUrl: string | null
  currentStepIndex: number
  description: string | null | undefined
  footerBottomPadding: number
  initialVisualAspectRatio: number
  onAudioLoadingChange: (isLoading: boolean) => void
  onNextPress: () => void | Promise<void>
  primaryLabel: string
  scrollContentBottomPadding: number
  status: {
    isAudioLoading: boolean
    isLastStep: boolean
    isNextDisabled?: boolean
    isSubmitting?: boolean
  }
  stepKeys: string[]
  stepTitle: string
  visualContentType: string
  visualDiagnostics?: VisualMediaPlayerDiagnostics
  visualFallbackAspectRatio: number
  visualUrl: string | null
}) {
  const { isAudioLoading, isLastStep, isNextDisabled, isSubmitting } = status
  const [audioRetryCount, setAudioRetryCount] = useState(0)
  const [hasAudioError, setHasAudioError] = useState(false)
  const audioPlayerRef = useRef<AudioPlayerHandle>(null)
  const visualMediaPlayerRef = useRef<VisualMediaPlayerHandle>(null)

  const handleRetryAudio = useCallback(() => {
    setHasAudioError(false)
    setAudioRetryCount((count) => count + 1)
  }, [])

  const handlePrimaryPress = useCallback(() => {
    if (isLastStep) {
      audioPlayerRef.current?.pause()
      visualMediaPlayerRef.current?.pause()
    }

    return onNextPress()
  }, [isLastStep, onNextPress])

  return (
    <>
      <ScrollView
        contentInset={{ bottom: scrollContentBottomPadding }}
        scrollIndicatorInsets={{ bottom: scrollContentBottomPadding }}
      >
        <Text className="text-xl font-bold mb-4 px-5 text-black">
          {currentStepIndex + 1}. {stepTitle}
        </Text>

        {visualUrl != null && visualContentType.startsWith('video/') && (
          <VisualMediaPlayer
            ref={visualMediaPlayerRef}
            key={`visual-${currentStepIndex}`}
            source={{ uri: visualUrl }}
            contentType={visualContentType}
            diagnostics={visualDiagnostics}
            aspectRatio={initialVisualAspectRatio}
            fallbackAspectRatio={visualFallbackAspectRatio}
          />
        )}

        {visualUrl != null && visualContentType.startsWith('image/') && (
          <MediaImage
            key={`image-${currentStepIndex}`}
            source={{ uri: visualUrl }}
            contentType={visualContentType}
            aspectRatio={initialVisualAspectRatio}
            fallbackAspectRatio={visualFallbackAspectRatio}
          />
        )}

        {audioUrl != null && (
          <View className="relative w-full">
            <AudioPlayer
              ref={audioPlayerRef}
              key={`audio-${currentStepIndex}-${audioRetryCount}`}
              source={{ uri: audioUrl }}
              autoPlay
              onLoadingChange={onAudioLoadingChange}
              onErrorChange={setHasAudioError}
            />

            {isAudioLoading && !hasAudioError && (
              <View
                className="absolute inset-0 items-center justify-center bg-black/5 rounded-lg"
                style={{ pointerEvents: 'none' }}
              >
                <Spinner size="small" />
              </View>
            )}

            {hasAudioError && (
              <View className="mx-4 mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <Text className="text-sm font-medium text-amber-900">
                  Unable to load media
                </Text>
                <Pressable
                  onPress={handleRetryAudio}
                  className="mt-2 self-start rounded-md bg-amber-500 px-3 py-2"
                  accessibilityRole="button"
                  accessibilityLabel="Retry audio"
                >
                  <Text className="text-sm font-semibold text-white">
                    Retry
                  </Text>
                </Pressable>
              </View>
            )}
          </View>
        )}

        {description != null && (
          <MarkdownText
            content={description}
            style={{ paddingHorizontal: 20, marginBottom: 20 }}
          />
        )}
        <View style={{ height: scrollContentBottomPadding }} />
      </ScrollView>

      <View
        className="absolute bottom-0 left-0 right-0 bg-background-primary p-5"
        style={{ paddingBottom: footerBottomPadding }}
      >
        <Pressable
          className={`w-full py-4 items-center justify-center mb-6 ${
            isNextDisabled ? 'opacity-60' : ''
          }`}
          style={{
            backgroundColor: '#291180',
            borderRadius: 8,
          }}
          accessibilityRole="button"
          disabled={isNextDisabled}
          onPress={handlePrimaryPress}
        >
          {isSubmitting ? (
            <Spinner size="small" color="#ffffff" />
          ) : (
            <Text className="text-base font-semibold text-primary-foreground">
              {primaryLabel}
            </Text>
          )}
        </Pressable>

        <SessionStepIndicator
          stepKeys={stepKeys}
          currentStepIndex={currentStepIndex}
          activeColor="#291180"
        />
      </View>
    </>
  )
}
